import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'
import { absoluteUrl, businessSchema, websiteSchema } from '@/lib/schema'

const serialize = (data: Record<string, unknown>): string => JSON.stringify(data).replace(/</g, '\\u003c')

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    path = '/',
    noindex = false,
    type = 'website',
    jsonLd = [],
    publishedTime,
    modifiedTime
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const canonical = absoluteUrl(path)
    const image = absoluteUrl(ogImage ?? siteConfig.ogImage ?? '/images/acm/na.webp')
    const structured = [businessSchema(), websiteSchema(), ...jsonLd]

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta key='description' name='description' content={description} />
            <link key='canonical' rel='canonical' href={canonical} />
            {noindex && <meta key='robots' name='robots' content='noindex, follow' />}

            <meta key='og:title' property='og:title' content={pageTitle} />
            <meta key='og:description' property='og:description' content={description} />
            <meta key='og:type' property='og:type' content={type} />
            <meta key='og:url' property='og:url' content={canonical} />
            <meta key='og:site_name' property='og:site_name' content={siteConfig.name} />
            <meta key='og:locale' property='og:locale' content='en_US' />
            <meta key='og:image' property='og:image' content={image} />
            <meta key='twitter:card' name='twitter:card' content='summary_large_image' />
            <meta key='twitter:title' name='twitter:title' content={pageTitle} />
            <meta key='twitter:description' name='twitter:description' content={description} />
            <meta key='twitter:image' name='twitter:image' content={image} />
            {publishedTime && <meta key='article:published_time' property='article:published_time' content={publishedTime} />}
            {modifiedTime && <meta key='article:modified_time' property='article:modified_time' content={modifiedTime} />}

            {structured.map((data, index) => (
                <script
                    key={`ld-${index}`}
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: serialize(data) }}
                />
            ))}
        </Head>
    )
}
